import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import SEOHead from './SEOHead';

const {
  FiArrowRight, FiFeather, FiEdit3, FiCheckCircle, FiSearch, FiMessageSquare, FiZap, FiBookOpen, FiType,
  FiList, FiTrendingUp, FiUploadCloud, FiFileText,
} = FiIcons;

// C1 — Book Intelligence: per-chapter summaries written in the author's OWN voice (live: /books/[id]/chapter-summaries)
const chapterSummaries = [
  { n: '1', t: 'The Doorway', s: 'She rehearses an apology that the weather, and Theo, make pointless before it is spoken.' },
  { n: '2', t: 'Low Tide', s: 'An old debt resurfaces; the town keeps its secrets the way the sea keeps its dead — patiently.' },
  { n: '3', t: 'What the House Knew', s: 'Returning home, she learns the rooms remember more than the people who left them.' },
];

// C2 — Author Style KB: rules distilled from the author's own accept/reject/edit decisions (live: style/rules, distillStyleRules)
const styleRules = [
  { rule: 'Prefer the active voice in action beats', from: 'learned from 14 of your edits', on: true },
  { rule: 'Keep em-dashes for interruption, not emphasis', from: 'learned from 9 of your edits', on: true },
  { rule: 'Trim “very / really / just” unless in dialogue', from: 'learned from 23 of your edits', on: true },
  { rule: 'Open chapters on an image, not a thought', from: 'suggested — pending your nod', on: false },
];

// C3 — Import existing manuscript (live: /api/v1/import via pandoc)
const importFormats = ['DOCX', 'EPUB', 'ODT', 'Markdown', 'HTML'];

// a mock of the live editor highlighting (Hemingway-style, but it knows your voice too)
const highlighted = [
  { t: 'Theo arrived the way weather does', c: '' },
  { t: ' — without asking', c: '' },
  { t: ', and it was very impossible to ignore.', c: 'filler' },
  { t: ' She had basically spent the whole morning rehearsing what she would eventually say to him in the doorway when he finally turned up at last.', c: 'hard' },
];
const tint = {
  filler: 'underline decoration-pink-400 decoration-2 underline-offset-2',
  hard: 'bg-yellow-100',
};

const features = [
  { icon: FiZap, t: 'Live style highlighting', d: 'Adverbs, passive voice, filler, echoes and genuinely hard sentences — flagged as you write, calibrated so simple prose is never redlined.' },
  { icon: FiCheckCircle, t: 'Grammar corrector', d: 'Click a wavy underline to fix spelling and grammar — self-hosted, private, no third-party sharing.' },
  { icon: FiFeather, t: 'Ideas in your voice', d: 'Select a line and ✨ Ideas suggests a sharper version, a different angle, or a next sentence — written from your own prose, not a generic model.' },
  { icon: FiEdit3, t: 'Rewrite in your voice', d: 'Turn a rough line into your voice — measured from your manuscript, with a model selector when you want a second opinion.' },
  { icon: FiSearch, t: 'Entity research, cited', d: 'Click any name, place or thing for an instant, book-aware definition — plus deep research with numbered, credible sources.' },
  { icon: FiMessageSquare, t: 'Comments & collaboration', d: 'Inline comments as a chat thread, live presence, and sharing — co-edit with an editor like a doc.' },
];

const WritingStudioPage = () => {
  const structuredData = {
    '@context': 'https://schema.org', '@type': 'WebPage',
    name: 'Writing Studio — ManuscriptSmith',
    description: 'A distraction-free manuscript editor with live style highlighting, grammar correction, in-your-voice ideas and rewrites, and cited research — built for authors.',
    url: 'https://manuscriptsmith.com/writing-studio',
  };
  return (
    <div className="min-h-screen bg-gradient-to-br from-parchment-50 to-parchment-100">
      <SEOHead
        title="Writing Studio — Write in Your Own Voice, With Live Feedback | ManuscriptSmith"
        description="ManuscriptSmith's editor highlights style as you write, fixes grammar, suggests ideas and rewrites in YOUR voice (measured from your manuscript), and researches any reference with cited sources."
        keywords="manuscript editor, writing software for authors, write in my voice, ai writing assistant authors, book writing app, style checker novel"
        canonicalUrl="https://manuscriptsmith.com/writing-studio"
        structuredData={structuredData}
      />
      <main>
        {/* Hero */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-10 text-center">
          <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-secondary-700 bg-secondary-50 px-3 py-1 rounded-full mb-4">
            <SafeIcon icon={FiFeather} className="w-4 h-4" /> Writing Studio
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Write in <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">your own voice</span> — with feedback as you type.
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            A calm, distraction-free editor that catches weak style, fixes grammar, and — unlike every "AI writer" —
            suggests ideas and rewrites measured from <em>your</em> manuscript, not a generic model.
          </p>
          <div className="mt-7 flex items-center justify-center gap-3">
            <Link to="/signup" className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-lg hover:from-primary-600 hover:to-secondary-600">
              Get started <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" />
            </Link>
            <Link to="/voice-dna" className="inline-flex items-center px-6 py-3 border border-gray-300 text-gray-700 font-semibold rounded-lg hover:bg-white">See Voice DNA</Link>
          </div>
        </section>

        {/* Live editor mock */}
        <section className="bg-white py-16">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-8">
              <SafeIcon icon={FiType} className="w-8 h-8 text-primary-500 mx-auto mb-3" />
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Feedback as you write — not after</h2>
              <p className="text-gray-600 max-w-2xl mx-auto">Style flags appear inline. Click a chip to focus just that kind. Select a line for ideas in your voice.</p>
            </div>
            <div className="rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
              <div className="border-b border-gray-100 bg-parchment-50 px-4 py-2 flex items-center gap-2 text-xs">
                <span className="px-2 py-0.5 rounded-full bg-emerald-600 text-white font-semibold">Style ON</span>
                <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">Grammar</span>
                <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-500">✨ Ideas</span>
              </div>
              <div className="p-6 text-[15px] leading-loose text-gray-800" style={{ fontFamily: 'Georgia, serif' }}>
                {highlighted.map((s, i) => <span key={i} className={tint[s.c] || ''}>{s.t}</span>)}
              </div>
              <div className="border-t border-gray-100 px-4 py-2 flex flex-wrap gap-1.5 text-[11px]">
                {[['Very hard', 'bg-red-400'], ['Hard to read', 'bg-yellow-400'], ['Adverbs', 'bg-blue-400'], ['Passive', 'bg-green-400'], ['Filler', 'bg-pink-400']].map(([l, c]) => (
                  <span key={l} className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-gray-50"><span className={`w-2.5 h-2.5 rounded-full ${c}`} />{l}</span>
                ))}
              </div>
            </div>
            {/* the ✨ Ideas popup, in your voice */}
            <div className="max-w-sm mx-auto -mt-2 relative z-10">
              <div className="rounded-xl border border-violet-200 bg-white shadow-xl p-3 mt-4">
                <div className="flex items-center gap-1.5 mb-2 text-sm"><span className="text-violet-500">✨</span><span className="font-bold text-gray-800">Ideas in your voice</span></div>
                {[['In your voice', 'Theo arrived like weather — unasked, impossible to ignore.'], ['Another angle', 'She heard him before she saw him: the door, the rain, the old apology of his footsteps.'], ['Keep going', 'Every careful sentence she had rehearsed left her at once.']].map(([k, t]) => (
                  <div key={k} className="rounded-lg border border-gray-200 px-2.5 py-1.5 mb-1.5 hover:border-violet-300 hover:bg-violet-50">
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-violet-500">{k}</div>
                    <div className="text-[12.5px] text-gray-700 leading-snug">{t}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Feature grid */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Everything an author actually needs</h2>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {features.map((f) => (
                <div key={f.t} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm">
                  <SafeIcon icon={f.icon} className="w-6 h-6 text-secondary-600 mb-3" />
                  <div className="font-semibold text-gray-800">{f.t}</div>
                  <div className="text-sm text-gray-500 mt-1">{f.d}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* C1 — Book Intelligence: chapter summaries in your voice */}
        <section className="bg-white py-16 border-t border-parchment-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div>
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-secondary-700 bg-secondary-50 px-3 py-1 rounded-full mb-4">
                  <SafeIcon icon={FiList} className="w-4 h-4" /> Book Intelligence
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-3">Every chapter, summarized in <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">your</span> voice</h2>
                <p className="text-gray-600 mb-4">
                  One click writes a faithful one-line summary of every chapter — not in flat, robotic English, but in the
                  same voice you wrote the book in. Perfect for keeping your plot straight, briefing a cover, or writing a blurb.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  {['Generated from the full chapter text — never invented', 'Fill only the missing ones, or regenerate all', 'Feeds your cover brief and back-copy'].map((x) => (
                    <li key={x} className="flex items-start gap-2"><SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" /> {x}</li>
                  ))}
                </ul>
              </div>
              <div className="space-y-3">
                {chapterSummaries.map((c, i) => (
                  <motion.div key={c.n}
                    initial={{ opacity: 0, y: 14 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                    transition={{ duration: 0.4, delay: i * 0.08 }}
                    className="rounded-2xl border border-gray-200 bg-gradient-to-br from-parchment-50 to-white p-4 shadow-sm">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="w-6 h-6 rounded-md bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">{c.n}</span>
                      <span className="font-semibold text-gray-800 text-sm">{c.t}</span>
                    </div>
                    <p className="text-[13px] text-gray-600 leading-snug" style={{ fontFamily: 'Georgia, serif' }}>{c.s}</p>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* C2 — Author Style KB: it learns your style */}
        <section className="py-16">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid lg:grid-cols-2 gap-10 items-center">
              <div className="order-2 lg:order-1">
                <div className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
                  <div className="border-b border-gray-100 bg-parchment-50 px-4 py-2 flex items-center gap-2 text-xs">
                    <SafeIcon icon={FiTrendingUp} className="w-4 h-4 text-primary-500" />
                    <span className="font-semibold text-gray-700">Your Style Rules</span>
                    <span className="ml-auto text-gray-400">learns as you write</span>
                  </div>
                  <div className="divide-y divide-gray-100">
                    {styleRules.map((r, i) => (
                      <motion.div key={r.rule}
                        initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }}
                        transition={{ duration: 0.35, delay: i * 0.07 }}
                        className="flex items-center gap-3 px-4 py-3">
                        <span className={`w-9 h-5 rounded-full flex-shrink-0 relative transition-colors ${r.on ? 'bg-primary-500' : 'bg-gray-200'}`}>
                          <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-white shadow transition-all ${r.on ? 'left-4' : 'left-0.5'}`} />
                        </span>
                        <div className="min-w-0">
                          <div className="text-sm text-gray-800 leading-snug">{r.rule}</div>
                          <div className="text-[11px] text-gray-400">{r.from}</div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </div>
              <div className="order-1 lg:order-2">
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-primary-700 bg-primary-50 px-3 py-1 rounded-full mb-4">
                  <SafeIcon icon={FiZap} className="w-4 h-4" /> It learns your style
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-3">Every edit you make teaches it</h2>
                <p className="text-gray-600 mb-4">
                  When you accept, tweak, or reject a suggestion, ManuscriptSmith notices. It quietly distills your choices into a
                  private set of style rules — so the next suggestion is already more <em>you</em>. Toggle a rule off any time;
                  it never decides for you.
                </p>
                <ul className="space-y-2 text-sm text-gray-600">
                  {['Built from your real decisions, not a template', 'Carries across every book you write', 'Yours and private — never used to train other models'].map((x) => (
                    <li key={x} className="flex items-start gap-2"><SafeIcon icon={FiCheckCircle} className="w-4 h-4 text-primary-500 mt-0.5 flex-shrink-0" /> {x}</li>
                  ))}
                </ul>
                <Link to="/author-persona" className="inline-flex items-center mt-5 text-primary-600 font-semibold hover:underline">
                  See how your Author Persona works <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-1.5" />
                </Link>
              </div>
            </div>
          </div>
        </section>

        {/* C3 — Import your existing manuscript */}
        <section className="py-14 bg-gradient-to-br from-gray-900 to-primary-900 text-white">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center gap-8">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="flex-shrink-0 w-20 h-20 rounded-2xl bg-white/10 border border-white/15 flex items-center justify-center">
              <SafeIcon icon={FiUploadCloud} className="w-10 h-10 text-secondary-300" />
            </motion.div>
            <div className="flex-1 text-center md:text-left">
              <div className="text-xs font-bold uppercase tracking-wide text-secondary-300 mb-2">Already started?</div>
              <h2 className="text-2xl md:text-3xl font-bold mb-2">Bring your manuscript — we'll set up your book in seconds</h2>
              <p className="opacity-90 mb-4">
                Drop in a file and ManuscriptSmith splits it into parts and chapters, cleans up the formatting, and even finds
                your real cover — so you start writing, not reformatting.
              </p>
              <div className="flex flex-wrap gap-2 justify-center md:justify-start">
                {importFormats.map((f) => (
                  <span key={f} className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white/10 border border-white/15 text-sm">
                    <SafeIcon icon={FiFileText} className="w-3.5 h-3.5 text-secondary-300" /> {f}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16 text-center">
          <SafeIcon icon={FiBookOpen} className="w-8 h-8 text-primary-500 mx-auto mb-3" />
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Your book, in your voice — finished.</h2>
          <p className="text-gray-600 mb-6">Write, refine, research, design the cover, and export KDP-ready — all in one studio.</p>
          <Link to="/signup" className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-primary-500 to-secondary-500 text-white font-semibold rounded-lg hover:from-primary-600 hover:to-secondary-600">
            Get started <SafeIcon icon={FiArrowRight} className="w-4 h-4 ml-2" />
          </Link>
        </section>
      </main>
    </div>
  );
};

export default WritingStudioPage;
